import { FC } from "react";

/**
 * Props for FirstRunExperience component
 */
export interface FirstRunExperienceProps {
  /** Callback when user clicks "Get Started" button */
  onGetStarted: () => void;
}

/**
 * First-run experience component displaying a value placemat welcome screen.
 *
 * Follows Microsoft Office Add-ins value placemat pattern:
 * - Logo prominently placed at top
 * - Clear value proposition
 * - Feature highlights/summary
 * - Call-to-action button
 *
 * Requirements: FR-001 through FR-012
 */
export const FirstRunExperience: FC<FirstRunExperienceProps> = ({ onGetStarted }) => {
  const handleGetStarted = async () => {
    try {
      await onGetStarted();
    } catch (err) {
      // Error is handled by auth context and displayed via error prop
      console.error("Get Started error:", err);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 bg-base-100"
      role="main"
      aria-label="Bienvenue sur Larry"
    >
      {/* Logo - FR-002: Prominently placed at top */}
      <div className="mb-8" role="img" aria-label="Larry logo">
        <img src="/assets/icon-64.png" alt="Larry Logo" className="w-16 h-16 mx-auto" />
      </div>

      {/* Value Proposition - FR-003: Clearly stated, prominent, easy-to-read */}
      <h1 className="text-2xl font-bold text-center mb-4 text-base-content">
        Assistant IA pour le travail juridique
      </h1>

      {/* Feature Highlights - FR-004: Concise, scannable format */}
      <div className="mb-8 w-full max-w-sm">
        <ul className="space-y-3 text-base-content/80" role="list">
          <li className="flex items-start" role="listitem">
            <span className="mr-2" aria-hidden="true">
              💬
            </span>
            <span>Chatbot juridique pour des réponses instantanées</span>
          </li>
          <li className="flex items-start" role="listitem">
            <span className="mr-2" aria-hidden="true">
              ✏️
            </span>
            <span>Correction intelligente du texte et suggestions</span>
          </li>
          <li className="flex items-start" role="listitem">
            <span className="mr-2" aria-hidden="true">
              📄
            </span>
            <span>Assistance et analyse de documents</span>
          </li>
        </ul>
      </div>

      {/* Call-to-Action Button - FR-005: Clear, prominent */}
      <button
        onClick={handleGetStarted}
        className="btn btn-primary btn-lg w-full max-w-sm uppercase tracking-wide font-bold"
        aria-label="Commencer avec Larry"
        type="button"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleGetStarted();
          }
        }}
      >
        Commencer
      </button>

      {/* FR-006: Single-screen layout (not carousel) */}
      {/* FR-007: Simple, brief, focused on essential actions and benefits */}
      {/* FR-011: Visually engaging with proper spacing and typography */}
      {/* FR-012: Responsive within task pane constraints (300-400px width) */}
    </div>
  );
};
